/**
 * dateFormat — show Excel date cells in the workbook's OWN number format.
 *
 * ExcelJS hands date cells over as JS Date objects and `cell.text` is the
 * JavaScript Date string ("Fri Jan 05 2024 05:30:00 GMT+0530 …"), so the
 * viewer showed that instead of e.g. "05-Jan-2024" (QA: "date format of the
 * original file is not displayed"). Formatting goes through SheetJS's SSF —
 * the same number-format engine SheetJS uses to render Excel formats.
 */
import * as XLSX from "xlsx";

const SSF = XLSX.SSF;
const MS_PER_DAY = 86400000;
const EPOCH_OFFSET = 25569; // days between 1899-12-30 and 1970-01-01

// Excel's locale-dependent built-in date formats, as ExcelJS reports them
// (numFmtId 14 / 22). Excel renders these with the PC's short-date pattern,
// so they are formatted with the browser locale instead of literally.
const LOCALE_SHORT_DATE = { "mm-dd-yy": true, "m/d/yy": true, "m/d/yyyy": false };
const LOCALE_SHORT_DATETIME = { "m/d/yy h:mm": true, "m/d/yy hh:mm": true };

/** True when an Excel number format string displays a date/time. */
export function isDateFormat(fmt) {
  if (!fmt || typeof fmt !== "string" || fmt === "General") return false;
  try { return !!SSF.is_date(fmt); } catch (e) { return false; }
}

/** JS Date (as ExcelJS returns it, UTC-based) -> Excel serial number. */
export function dateToSerial(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d.getTime() / MS_PER_DAY + EPOCH_OFFSET;
}

function serialToUtcDate(serial) {
  return new Date(Math.round((serial - EPOCH_OFFSET) * MS_PER_DAY));
}

/** Format an Excel serial with the cell's number format. */
export function formatExcelDate(fmt, serial) {
  if (typeof serial !== "number" || !Number.isFinite(serial)) return "";
  try {
    if (LOCALE_SHORT_DATE[fmt] || LOCALE_SHORT_DATETIME[fmt]) {
      const d = serialToUtcDate(serial);
      const opts = { timeZone: "UTC", day: "2-digit", month: "2-digit", year: "numeric" };
      if (LOCALE_SHORT_DATETIME[fmt]) Object.assign(opts, { hour: "numeric", minute: "2-digit" });
      return new Intl.DateTimeFormat(undefined, opts).format(d);
    }
    return SSF.format(fmt, serial);
  } catch (e) {
    try { return SSF.format("dd-mm-yyyy", serial); } catch (e2) { return String(serial); }
  }
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/**
 * Parse what a reader typed into a DATE-formatted cell back to an Excel
 * serial (so the saved file keeps a real date). Accepts ISO yyyy-mm-dd,
 * numeric d/m/y or m/d/y (order taken from the cell's format), and
 * "5-Jan-2024" / "Jan 5, 2024" style month names. Returns null otherwise.
 */
export function parseDateInput(text, fmt) {
  const s = String(text == null ? "" : text).trim();
  if (!s) return null;
  const mk = (y, m, d) => {
    let yy = y;
    if (yy < 100) yy += yy < 30 ? 2000 : 1900;
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    const t = Date.UTC(yy, m - 1, d);
    const back = new Date(t);
    if (back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
    return t / MS_PER_DAY + EPOCH_OFFSET;
  };
  let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (m) return mk(+m[1], +m[2], +m[3]);
  m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/.exec(s);
  if (m) {
    const f = String(fmt || "").toLowerCase().replace(/\[[^\]]*\]/g, "");
    const di = f.indexOf("d");
    const mi = f.indexOf("m");
    let dayFirst = di !== -1 && mi !== -1 ? di < mi : true;
    // Locale short date (id 14): follow the browser's order.
    if (LOCALE_SHORT_DATE[fmt] || LOCALE_SHORT_DATETIME[fmt]) {
      try {
        const parts = new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "2-digit", year: "numeric" })
          .formatToParts(new Date(2000, 10, 22));
        dayFirst = parts.findIndex((p) => p.type === "day") < parts.findIndex((p) => p.type === "month");
      } catch (e) { /* keep default */ }
    }
    return dayFirst ? mk(+m[3], +m[2], +m[1]) : mk(+m[3], +m[1], +m[2]);
  }
  m = /^(\d{1,2})[-\s/]([A-Za-z]{3,})[-\s/,]+(\d{2,4})$/.exec(s);
  if (m) {
    const mo = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase());
    if (mo !== -1) return mk(+m[3], mo + 1, +m[1]);
  }
  m = /^([A-Za-z]{3,})[-\s/]+(\d{1,2}),?[-\s/]+(\d{2,4})$/.exec(s);
  if (m) {
    const mo = MONTHS.indexOf(m[1].slice(0, 3).toLowerCase());
    if (mo !== -1) return mk(+m[3], mo + 1, +m[2]);
  }
  return null;
}

/**
 * Format any value with an Excel number format code ("0%", "$#,##0",
 * "mmm-yy", …). "General" / empty formats return null so callers can apply
 * their own default.
 */
export function formatWithExcelFormat(fmt, value) {
  if (!fmt || fmt === "General" || typeof value !== "number" || !Number.isFinite(value)) return null;
  try {
    if (LOCALE_SHORT_DATE[fmt] || LOCALE_SHORT_DATETIME[fmt]) return formatExcelDate(fmt, value);
    return SSF.format(fmt, value);
  } catch (e) {
    return null;
  }
}

const NUMERIC_TEXT = /^\s*-?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?\s*$/i;

/**
 * Display text for a NUMERIC cell, the way Excel shows it:
 *  - with the cell's number format ("$#,##0.00", "0.0%", "#,##0", …), or
 *  - "General": at most ~11 characters, so 0.1+0.2 shows 0.3 and
 *    2/3 shows 0.666666667 instead of 0.30000000000000004 / 0.6666666666666666
 *    (QA: "chart data is not rounding properly").
 * Non-numeric values are returned unchanged.
 */
export function formatNumberCell(fmt, value) {
  if (value === "" || value == null) return value;
  let n;
  if (typeof value === "number") n = value;
  else if (typeof value === "string" && NUMERIC_TEXT.test(value)) n = parseFloat(value);
  else return value;
  if (!Number.isFinite(n)) return value;
  try {
    return SSF.format(fmt && fmt !== "" ? fmt : "General", n);
  } catch (e) {
    try { return SSF.format("General", n); } catch (e2) { return value; }
  }
}
